const baseURL = process.env.REACT_APP_API_URL;

const getTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
        return 'Asia/Jerusalem';
    }
};

/**
 * Stream a program/workout-building session to the AI assistant.
 *
 * @param {Object} params
 * @param {string} params.message - the user's latest message
 * @param {Array} params.history - [{role:'user'|'assistant', content:string}, ...] (raw, with markers)
 * @param {number|null} params.templateId
 * @param {Object|null} params.programContext - {gender, experienceLevel, bodyPart, equipmentType, ...}
 * @param {Object|null} params.currentPreview - {workouts:[...]} or null
 * @param {Object} callbacks - {onStatus, onToken, onMarker, onSearchResults, onComplete, onError}
 * @returns {AbortController}
 */
export const streamWorkoutAi = ({ message, history, templateId, programContext, currentPreview }, { onStatus, onToken, onMarker, onSearchResults, onComplete, onError }) => {
    const token = localStorage.getItem('auth');
    const controller = new AbortController();

    fetch(`${baseURL}/admin/workout-ai/stream`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Timezone': getTimezone(),
        },
        body: JSON.stringify({
            message,
            history,
            templateId: templateId ?? null,
            programContext: programContext ?? null,
            currentPreview: currentPreview ?? null,
        }),
        signal: controller.signal,
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            const read = () => {
                reader.read().then(({ done, value }) => {
                    if (done) return;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    let currentEventType = null;

                    for (const line of lines) {
                        if (line.startsWith('event:')) {
                            currentEventType = line.substring(6).trim();
                        } else if (line.startsWith('data:')) {
                            const data = line.substring(5).trim();
                            if (!data) continue;

                            try {
                                const parsed = JSON.parse(data);
                                const eventType = currentEventType || parsed.type;

                                switch (eventType) {
                                    case 'status':
                                        onStatus?.(parsed.status);
                                        break;
                                    case 'token':
                                        onToken?.(parsed.text);
                                        break;
                                    case 'marker':
                                        onMarker?.(parsed.marker);
                                        break;
                                    case 'search_results':
                                        onSearchResults?.(parsed.marker);
                                        break;
                                    case 'complete':
                                        onComplete?.(parsed.fullResponse);
                                        break;
                                    case 'error':
                                        onError?.(parsed.message || 'שגיאה לא ידועה');
                                        break;
                                    default:
                                        break;
                                }
                            } catch (e) {
                                // skip unparseable lines
                            }

                            currentEventType = null;
                        }
                    }

                    read();
                }).catch(err => {
                    if (err.name !== 'AbortError') {
                        onError?.(err.message || 'שגיאת חיבור');
                    }
                });
            };

            read();
        })
        .catch(err => {
            if (err.name !== 'AbortError') {
                onError?.(err.message || 'שגיאת חיבור');
            }
        });

    return controller;
};
